import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Typography,
    Box,
    Divider,
    Chip,
    Tabs,
    Tab
} from '@mui/material';
import {
    Close as CloseIcon,
    Person as PersonIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    Height as HeightIcon,
    FitnessCenter as WeightIcon,
    MonitorWeight as BMIIcon,
    LocationOn as LocationIcon,
    MedicalServices as SymptomsIcon,
    Assessment as AssessmentIcon,
    Info as InfoIcon,
    DateRange as DateIcon,
    DirectionsRun as ExerciseIcon,
    Psychology as GoalIcon,
    Notifications as NotificationIcon,
    Watch as DeviceIcon,
    Warning as WarningIcon,
    Notes as NotesIcon
} from '@mui/icons-material';
import api from '../../api/api';
import en_ja from '../../en-ja-map';

const PatientDetailsDialog = ({ open, onClose, patientData, appointmentData }) => {
    const [userQuery, setUserQuery] = useState([]);
    const [activeTab, setActiveTab] = useState(0);
    const [finalConsultationAnswers, setFinalConsultationAnswers] = useState([]);
    const [userHealthData, setUserHealthData] = useState(null);
    const [notesHistory, setNotesHistory] = useState([]);

    const getQuestionsOne = () => {
        // const controller = new AbortController();

        // api.get(`/v1/auth/new/user/health-answers/${patientData.id}`, { signal: controller.signal })

        api.get(`/v1/auth/new/user/health-answers/${patientData.id}`)
            .then((res) => {
                setUserQuery(res.data.data.answers || []);
                setUserHealthData(res.data.data.user || []);
            })
            .catch((err) => {
                if (err.name !== 'CanceledError') {
                    console.error('Failed to fetch patient data:', err);
                }
            });

        // return () => controller.abort();
    }

    const getQuestionsTwo = () => {
        api.get(`v1/user/get/final-consulation/answers/${patientData.id}`)
            .then((res) => {
                setFinalConsultationAnswers(res.data.data.consultation_answers || []);
            })
            .catch((err) => {
                if (err.name !== 'CanceledError') {
                    console.error('Failed to fetch patient data:', err);
                }
            });
    }

    const getNotesHistory = () => {
        api.get(`/v1/clinic/user/notes/${patientData.id}`)
            .then((res) => {
                setNotesHistory(res.data?.data || []);
            })
            .catch((err) => {
                console.error(err);
            });
    };

    useEffect(() => {
        console.log('PatientDetailsDialog props changed:', { patientData, appointmentData });
        if (!open || !patientData?.id) return;

        getQuestionsOne();
        getQuestionsTwo();

        getNotesHistory();

    }, [open, patientData?.id]);

    if (!patientData || !appointmentData) return null;

    const rawCheckin = appointmentData.clinic_checkin_data;
    const checkinData = typeof rawCheckin === 'string'
        ? (() => { try { return JSON.parse(rawCheckin) } catch { return {} } })()
        : (rawCheckin || {});

    const genderValue = userHealthData?.gender ?? checkinData?.sex;
    const genderDisplay = genderValue == '0'
        ? (en_ja['Male'] || 'Male')
        : genderValue == '1'
            ? (en_ja['Female'] || 'Female')
            : null;

    // Extract height/weight from userQuery (Initial Health Assessment) - use as primary source
    const heightWeightQuestion = userQuery.find(q => q.question_id === 4 || q.question_text?.toLowerCase().includes('height'));
    const heightFromQuery = heightWeightQuestion?.types?.find(t => t.type === 'Height')?.answer_text;
    const weightFromQuery = heightWeightQuestion?.types?.find(t => t.type === 'Weight')?.answer_text;


    const heightCm = Number(heightFromQuery) || Number(userHealthData?.height) || Number(checkinData?.height) || null;
    const weightKg = Number(weightFromQuery) || Number(userHealthData?.weight) || Number(checkinData?.weight) || null;

    const providedBMI = Number(checkinData?.bmi);
    const bmiValue = Number.isFinite(providedBMI)
        ? providedBMI
        : (heightCm && weightKg
            ? (weightKg / Math.pow(heightCm / 100, 2))
            : null);

    const normalizeBMI = (bmi) => {
        if (!Number.isFinite(bmi)) return null;
        return bmi > 1000 ? +(bmi / 1000).toFixed(1) : +bmi.toFixed(1);
    };

    const safeBMI = normalizeBMI(bmiValue);

    const getBMICategory = (bmi) => {
        if (!Number.isFinite(bmi)) return null;
        const n = bmi > 1000 ? bmi / 1000 : bmi;
        if (n < 18.5) return { label: (en_ja['Underweight'] || 'Underweight'), color: '#2196F3' };
        if (n < 25) return { label: (en_ja['Normal'] || 'Normal'), color: '#4CAF50' };
        if (n < 30) return { label: (en_ja['Overweight'] || 'Overweight'), color: '#FF9800' };
        return { label: (en_ja['Obese'] || 'Obese'), color: '#f44336' };
    };

    const bmiCategory = getBMICategory(bmiValue);

    const hasAnyMedical =
        Boolean(heightCm) || Boolean(weightKg) || Number.isFinite(safeBMI) ||
        Boolean(checkinData?.sex) || Boolean(checkinData?.address) || Boolean(checkinData?.symptoms);

    const hasNotes = Boolean(appointmentData?.notes?.trim());

    const getQuestionIcon = (questionText) => {
        const text = questionText.toLowerCase();
        if (text.includes('age') || text.includes('dob')) return <DateIcon sx={{ color: '#dc3545', fontSize: 20 }} />;
        if (text.includes('sex') || text.includes('gender')) return <PersonIcon sx={{ color: '#fd7e14', fontSize: 20 }} />;
        if (text.includes('height') || text.includes('weight')) return <HeightIcon sx={{ color: '#007bff', fontSize: 20 }} />;
        if (text.includes('exercise') || text.includes('activity')) return <ExerciseIcon sx={{ color: '#28a745', fontSize: 20 }} />;
        if (text.includes('goal') || text.includes('target')) return <GoalIcon sx={{ color: '#6f42c1', fontSize: 20 }} />;
        if (text.includes('device')) return <DeviceIcon sx={{ color: '#17a2b8', fontSize: 20 }} />;
        if (text.includes('notification')) return <NotificationIcon sx={{ color: '#ffc107', fontSize: 20 }} />;
        if (text.includes('medical') || text.includes('blood pressure')) return <SymptomsIcon sx={{ color: '#dc3545', fontSize: 20 }} />;
        return <AssessmentIcon sx={{ color: '#6c757d', fontSize: 20 }} />;
    };

    const getHealthWarnings = () => {
        const warnings = [];

        const targetWeightQ = userQuery.find(q =>
            q.question_text.includes('目標体重') ||
            q.question_text.toLowerCase().includes('target weight')
        );

        const heightWeightQ = userQuery.find(q =>
            q.question_text.includes('身長') ||
            q.question_text.toLowerCase().includes('height')
        );

        const ageQ = userQuery.find(q =>
            q.question_text.includes('年齢') ||
            q.question_text.toLowerCase().includes('age')
        );

        const conditionsQ = userQuery.find(q =>
            q.question_text.includes('該当') ||
            q.question_text.toLowerCase().includes('following')
        );

        const targetWeight = Number(targetWeightQ?.answer_text);

        const height =
            Number(heightWeightQ?.types?.find(t => t.type === 'Height')?.answer_text) ||
            Number(userHealthData?.height);

        const age = Number(ageQ?.answer_text);

        if (targetWeight && height && targetWeight < 40) {
            warnings.push({
                type: 'critical',
                message:
                    (en_ja['Target weight appears dangerously low. Medical review recommended.'] ||
                        'Target weight appears dangerously low. Medical review recommended.')
            });
        }

        if (age && conditionsQ && age < 25) {
            const hasHighBP = conditionsQ?.options?.some(opt =>
                opt.option_text.includes('血圧') ||
                opt.option_text.toLowerCase().includes('high blood pressure')
            );

            if (hasHighBP) {
                warnings.push({
                    type: 'warning',
                    message:
                        (en_ja['High blood pressure reported in young patient.'] ||
                            'High blood pressure reported in young patient.')
                });
            }
        }

        return warnings;
    };
    const healthWarnings = getHealthWarnings();

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '16px',
                    border: '1px solid #e9ecef',
                    boxShadow: '0 12px 32px rgba(0,0,0,0.12)',
                }
            }}
        >
            <DialogTitle sx={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #e9ecef', display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 2.5, px: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a1a', fontSize: '1.1rem', letterSpacing: '-0.02em' }}>
                    {en_ja['Patient Details'] || 'Patient Details'}
                </Typography>
                <IconButton onClick={onClose} size="small" sx={{
                    backgroundColor: 'white', width: 32, height: 32, border: '1px solid #e9ecef',
                    '&:hover': { backgroundColor: '#f8f9fa', borderColor: '#dc3545', '& .MuiSvgIcon-root': { color: '#dc3545' } }
                }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>

            <Box sx={{ p: 3, backgroundColor: '#f8f9fa', borderBottom: '1px solid #e9ecef' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ width: 40, height: 40, borderRadius: '10px', backgroundColor: '#dc3545', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 2 }}>
                        <PersonIcon sx={{ color: 'white', fontSize: 20 }} />
                    </Box>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a', fontSize: '1rem', mb: 0.5 }}>
                            {checkinData?.full_name || patientData?.name || '—'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666', fontSize: '0.8rem' }}>
                            {(en_ja['Patient ID:'] || 'Patient ID:')} {patientData?.id ?? '—'}
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', p: 1.5, backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                        <EmailIcon sx={{ color: '#dc3545', mr: 1, fontSize: 16 }} />
                        <Box>
                            <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.65rem' }}>
                                {en_ja['Email'] || 'Email'}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#1a1a1a', fontWeight: 500, fontSize: '0.8rem' }}>
                                {patientData?.email || '—'}
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', p: 1.5, backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                        <PhoneIcon sx={{ color: '#dc3545', mr: 1, fontSize: 16 }} />
                        <Box>
                            <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.65rem' }}>
                                {en_ja['Phone'] || 'Phone'}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#1a1a1a', fontWeight: 500, fontSize: '0.8rem' }}>
                                {patientData?.phone_number || '—'}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>

            <Box sx={{ borderBottom: '1px solid #e9ecef' }}>
                <Tabs
                    value={activeTab}
                    onChange={(e, newValue) => setActiveTab(newValue)}
                    sx={{
                        '& .MuiTab-root': {
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            color: '#6c757d',
                            '&.Mui-selected': {
                                color: '#dc3545'
                            }
                        },
                        '& .MuiTabs-indicator': {
                            backgroundColor: '#dc3545'
                        }
                    }}
                >
                    <Tab
                        icon={<InfoIcon fontSize="small" />}
                        label={en_ja['Details'] || 'Details'}
                        iconPosition="start"
                        sx={{ px: 3 }}
                    />
                    <Tab
                        icon={<AssessmentIcon fontSize="small" />}
                        label={en_ja['Health Assessment'] || 'Health Assessment'}
                        iconPosition="start"
                        sx={{ px: 3 }}
                    />
                    {/* {hasNotes && ( */}
                    <Tab
                        icon={<NotesIcon fontSize="small" />}
                        label={en_ja['Notes'] || 'Notes'}
                        iconPosition="start"
                        sx={{ px: 3 }}
                    />
                    {/* )} */}
                </Tabs>
            </Box>

            <DialogContent sx={{ p: 0 }}>

                {activeTab === 0 && (
                    <Box sx={{ p: 3 }}>

                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a', fontSize: '1rem', mb: 2 }}>
                            {en_ja['Medical Information'] || 'Medical Information'}
                        </Typography>

                        {!hasAnyMedical && (
                            <Box sx={{ p: 2, borderRadius: '12px', border: '1px dashed #e9ecef', color: '#666', fontSize: '0.875rem', mb: 3 }}>
                                {en_ja['No medical information available.'] || 'No medical information available.'}
                            </Box>
                        )}

                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 2, mb: 3 }}>
                            {heightCm && (
                                <Box sx={{ display: 'flex', alignItems: 'center', p: 2, backgroundColor: '#f8f9fa', borderRadius: '12px' }}>
                                    <HeightIcon sx={{ color: '#007bff', mr: 1.5, fontSize: 20 }} />
                                    <Box>
                                        <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem' }}>
                                            {en_ja['Height'] || 'Height'}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#1a1a1a', fontWeight: 500, fontSize: '0.875rem' }}>
                                            {heightCm.toFixed(1)} cm
                                        </Typography>
                                    </Box>
                                </Box>
                            )}

                            {weightKg && (
                                <Box sx={{ display: 'flex', alignItems: 'center', p: 2, backgroundColor: '#f8f9fa', borderRadius: '12px' }}>
                                    <WeightIcon sx={{ color: '#28a745', mr: 1.5, fontSize: 20 }} />
                                    <Box>
                                        <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem' }}>
                                            {en_ja['Weight'] || 'Weight'}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#1a1a1a', fontWeight: 500, fontSize: '0.875rem' }}>
                                            {weightKg.toFixed(1)} kg
                                        </Typography>
                                    </Box>
                                </Box>
                            )}

                            {Number.isFinite(safeBMI) && (
                                <Box sx={{ display: 'flex', alignItems: 'center', p: 2, backgroundColor: '#f8f9fa', borderRadius: '12px' }}>
                                    <BMIIcon sx={{ color: '#6f42c1', mr: 1.5, fontSize: 20 }} />
                                    <Box>
                                        <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem' }}>
                                            {en_ja['BMI'] || 'BMI'}
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body2" sx={{ color: '#1a1a1a', fontWeight: 500, fontSize: '0.875rem' }}>
                                                {safeBMI}
                                            </Typography>
                                            {bmiCategory && (
                                                <Chip
                                                    label={bmiCategory.label}
                                                    size="small"
                                                    sx={{ backgroundColor: bmiCategory.color, color: 'white', fontSize: '0.7rem', height: '20px', fontWeight: 600 }}
                                                />
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                            )}
                        </Box>

                        {genderDisplay && (
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2, mb: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', p: 2, backgroundColor: '#f8f9fa', borderRadius: '12px' }}>
                                    <PersonIcon sx={{ color: '#fd7e14', mr: 1.5, fontSize: 20 }} />
                                    <Box>
                                        <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem' }}>
                                            {en_ja['Gender'] || 'Gender'}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#1a1a1a', fontWeight: 500, fontSize: '0.875rem' }}>
                                            {genderDisplay}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        )}

                        {checkinData?.symptoms && (
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', p: 2, backgroundColor: '#fff3cd', borderRadius: '12px', border: '1px solid #ffeaa7' }}>
                                    <SymptomsIcon sx={{ color: '#856404', mr: 1.5, fontSize: 20, mt: 0.2 }} />
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="caption" sx={{ color: '#856404', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem', mb: 0.5, display: 'block' }}>
                                            {en_ja['Symptoms'] || 'Symptoms'}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#856404', fontWeight: 500, fontSize: '0.875rem', lineHeight: 1.5 }}>
                                            {checkinData.symptoms}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        )}
                    </Box>
                )}

                {activeTab === 1 && (
                    <Box sx={{ p: 3 }}>
                        {healthWarnings.length > 0 && (
                            <Box sx={{ mb: 3 }}>
                                {healthWarnings.map((warning, index) => (
                                    <Box key={index} sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5,
                                        p: 2,
                                        mb: index < healthWarnings.length - 1 ? 2 : 0,
                                        backgroundColor: warning.type === 'critical' ? '#f8d7da' : '#fff3cd',
                                        borderRadius: '12px',
                                        border: `1px solid ${warning.type === 'critical' ? '#f5c6cb' : '#ffeaa7'}`
                                    }}>
                                        <WarningIcon sx={{ color: warning.type === 'critical' ? '#721c24' : '#856404', fontSize: 20 }} />
                                        <Typography variant="body2" sx={{
                                            color: warning.type === 'critical' ? '#721c24' : '#856404',
                                            fontWeight: 500
                                        }}>
                                            {warning.message}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        )}

                        {userQuery.length > 0 && (
                            <>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a', fontSize: '1rem', mb: 2 }}>
                                    {en_ja['Initial Health Assessment'] || 'Initial Health Assessment'}
                                </Typography>
                                <Box sx={{ display: 'grid', gap: 2, mb: 4 }}>
                                    {userQuery.map((question) => (
                                        <Box key={question.question_id} sx={{
                                            p: 2,
                                            backgroundColor: '#f8f9fa',
                                            borderRadius: '12px',
                                            border: '1px solid #e9ecef'
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
                                                {getQuestionIcon(question.question_text)}
                                                <Typography variant="body2" fontWeight="600" sx={{ color: '#1a1a1a', flex: 1 }}>
                                                    {question.question_text}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ ml: 3.5 }}>
                                                {question.answer_text && (
                                                    <Typography variant="body2" sx={{
                                                        backgroundColor: 'white',
                                                        p: 1.5,
                                                        borderRadius: '8px',
                                                        border: '1px solid #e9ecef',
                                                        fontWeight: 500
                                                    }}>
                                                        {question.answer_text}
                                                    </Typography>
                                                )}
                                                {question.types && (
                                                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                                                        {question.types.map((type, index) => (
                                                            <Box key={index} sx={{
                                                                backgroundColor: 'white',
                                                                p: 1.5,
                                                                borderRadius: '8px',
                                                                border: '1px solid #e9ecef',
                                                                minWidth: '80px'
                                                            }}>
                                                                <Typography variant="caption" color="text.secondary" sx={{
                                                                    textTransform: 'uppercase',
                                                                    fontWeight: 600,
                                                                    fontSize: '0.7rem',
                                                                    display: 'block'
                                                                }}>
                                                                    {type.type}
                                                                </Typography>
                                                                <Typography variant="body2" fontWeight="500">
                                                                    {type.answer_text}
                                                                    {type.type === 'Height' && ' cm'}
                                                                    {type.type === 'Weight' && ' kg'}
                                                                </Typography>
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                )}
                                                {question.options && (
                                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                        {question.options.map((option, index) => (
                                                            <Chip
                                                                key={index}
                                                                label={option.option_text}
                                                                size="small"
                                                                sx={{
                                                                    backgroundColor: '#dc3545',
                                                                    color: 'white',
                                                                    fontWeight: 500,
                                                                    '& .MuiChip-label': {
                                                                        fontSize: '0.75rem'
                                                                    }
                                                                }}
                                                            />
                                                        ))}
                                                    </Box>
                                                )}
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            </>
                        )}

                        {finalConsultationAnswers.length > 0 && (
                            <>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a', fontSize: '1rem', mb: 2 }}>
                                    {en_ja['Final Consultation Assessment'] || 'Final Consultation Assessment'}
                                </Typography>
                                <Box sx={{ display: 'grid', gap: 2 }}>
                                    {finalConsultationAnswers.map((question) => (
                                        <Box key={question.question_id} sx={{
                                            p: 2,
                                            backgroundColor: '#e3f2fd',
                                            borderRadius: '12px',
                                            border: '1px solid #bbdefb'
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
                                                {getQuestionIcon(question.question_text)}
                                                <Typography variant="body2" fontWeight="600" sx={{ color: '#1a1a1a', flex: 1 }}>
                                                    {question.question_text}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ ml: 3.5 }}>

                                                {question.answer_text && (
                                                    <Typography variant="body2" sx={{
                                                        backgroundColor: 'white',
                                                        p: 1.5,
                                                        borderRadius: '8px',
                                                        border: '1px solid #bbdefb',
                                                        fontWeight: 500
                                                    }}>
                                                        {question.answer_text}
                                                    </Typography>
                                                )}

                                                {question.options && question.options.length > 0 && (
                                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                        {question.options.map((option) => (
                                                            <Chip
                                                                key={option.option_id}
                                                                label={option.option_text}
                                                                size="small"
                                                                sx={{
                                                                    backgroundColor: '#dc3545',
                                                                    color: 'white',
                                                                    fontWeight: 500,
                                                                    '& .MuiChip-label': {
                                                                        fontSize: '0.75rem'
                                                                    }
                                                                }}
                                                            />
                                                        ))}
                                                    </Box>
                                                )}


                                                {!question.answer_text && (!question.options || question.options.length === 0) && (
                                                    <Typography variant="body2" sx={{
                                                        backgroundColor: '#fff3e0',
                                                        p: 1.5,
                                                        borderRadius: '8px',
                                                        border: '1px solid #ffcc02',
                                                        fontWeight: 500,
                                                        color: '#e65100',
                                                        fontStyle: 'italic'
                                                    }}>
                                                        {en_ja['No response provided'] || 'No response provided'}
                                                    </Typography>
                                                )}

                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            </>
                        )}

                        {userQuery.length === 0 && finalConsultationAnswers.length === 0 && (
                            <Box sx={{ p: 2, borderRadius: '12px', border: '1px dashed #e9ecef', color: '#666', fontSize: '0.875rem', textAlign: 'center' }}>
                                {en_ja['No health assessment data available.'] || 'No health assessment data available.'}
                            </Box>
                        )}
                    </Box>
                )}

                {activeTab === 2 && (
                    <Box sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a', fontSize: '1rem', mb: 3 }}>
                            {en_ja['Consultation Notes'] || 'Consultation Notes'}
                        </Typography>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 2,
                            p: 3,
                            backgroundColor: '#f8f9fa',
                            borderRadius: '12px',
                            border: '1px solid #e9ecef'
                        }}>
                            <NotesIcon sx={{ color: '#dc3545', fontSize: 24, mt: 0.2 }} />
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="caption" sx={{
                                    color: '#666',
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                    fontSize: '0.7rem',
                                    mb: 1,
                                    display: 'block'
                                }}>
                                    {en_ja["Doctor's Notes"] || "Doctor's Notes"}
                                </Typography>
                                <Typography variant="body1" sx={{
                                    color: '#1a1a1a',
                                    fontWeight: 500,
                                    fontSize: '0.9rem',
                                    lineHeight: 1.6,
                                    whiteSpace: 'pre-wrap'
                                }}>
                                    <Box>
                                        {notesHistory?.length > 0 ? (
                                            notesHistory.map((item, index) => (
                                                <Box
                                                    key={index}
                                                    sx={{
                                                        pb: 1.5,
                                                        mb: 1.5,
                                                        borderBottom:
                                                            index !== notesHistory.length - 1
                                                                ? '1px solid #e9ecef'
                                                                : 'none'
                                                    }}
                                                >
                                                    <Box sx={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        mb: 0.5
                                                    }}>
                                                        <Typography variant="caption" color="#6c757d">
                                                            {new Date(item.notes_date)
                                                                .toLocaleDateString('en-CA')
                                                                .replace(/-/g, '/')}
                                                        </Typography>
                                                        
                                                        <Typography variant="caption" color="#0d6efd">
                                                            {new Date(item.notes_date).toLocaleTimeString([], {
                                                                hour: '2-digit',
                                                                minute: '2-digit',
                                                                second: '2-digit',
                                                                hour12: false
                                                            })}
                                                        </Typography>
                                                    </Box>

                                                    {/* Notes */}
                                                    <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                                                        {item.notes}
                                                    </Typography>
                                                </Box>
                                            ))
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                {en_ja["No previous notes available"]}
                                            </Typography>
                                        )}
                                    </Box>
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default PatientDetailsDialog;
